import { useState } from "react";
import { InboxOutlined } from "@ant-design/icons";
import {
  Checkbox,
  DatePicker,
  Form,
  Input,
  Modal,
  Radio,
  Select,
  Upload,
  message,
} from "antd";
import type { UploadFile } from "antd/es/upload/interface";
import type { Dayjs } from "dayjs";

import { ROLE_OPTIONS, type AssumedRole } from "@/contexts/AssumedRoleContext";
import {
  ALL_ROLES,
  type DocumentClass,
  type RolloutScope,
} from "@/views/DocumentWorkspace/documentWorkspaceData";
import type { UploadDocumentFormValues } from "@/views/DocumentWorkspace/documentWorkspace.types";

interface FormShape {
  title: string;
  documentClass: DocumentClass;
  rolloutScope: RolloutScope;
  amendmentLabel?: string;
  effectiveFrom?: Dayjs;
  note?: string;
}

/** Upload path. See docs/PRD_MOCKUPS_DESIGN_NOTES.md. */
export function UploadDocumentModal({
  open,
  onCancel,
  onUpload,
}: {
  open: boolean;
  onCancel: () => void;
  onUpload: (values: UploadDocumentFormValues) => void;
}) {
  const [form] = Form.useForm<FormShape>();
  const [roleAccess, setRoleAccess] = useState<AssumedRole[]>(ALL_ROLES);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [rolloutScope, setRolloutScope] = useState<RolloutScope>("study");

  const resetState = () => {
    form.resetFields();
    setRoleAccess(ALL_ROLES);
    setFileList([]);
    setRolloutScope("study");
  };

  return (
    <Modal
      open={open}
      title="Upload a study document"
      okText="Upload"
      width={640}
      onCancel={() => {
        resetState();
        onCancel();
      }}
      onOk={async () => {
        const file = fileList[0]?.originFileObj;
        if (!file) {
          message.error("Choose a file to upload.");
          return;
        }
        if (roleAccess.length === 0) {
          message.error("At least one role must have access.");
          return;
        }
        let values: FormShape;
        try {
          values = await form.validateFields();
        } catch {
          return;
        }
        onUpload({
          title: values.title,
          documentClass: values.documentClass,
          rolloutScope: values.rolloutScope,
          roleAccess,
          amendmentLabel: values.amendmentLabel?.trim() || null,
          effectiveFrom: values.effectiveFrom
            ? values.effectiveFrom.format("YYYY-MM-DD")
            : null,
          note: values.note?.trim() || null,
          file,
        });
        resetState();
      }}
      destroyOnHidden
    >
      <Upload.Dragger
        className="mb-4"
        multiple={false}
        maxCount={1}
        fileList={fileList}
        beforeUpload={() => false}
        onChange={({ fileList: next }) => setFileList(next.slice(-1))}
        onRemove={() => setFileList([])}
      >
        <p className="text-[28px] text-[#0C98D0]">
          <InboxOutlined />
        </p>
        <p className="text-[13px] font-medium text-[#1F2933]">
          Drop a file here, or browse
        </p>
        <p className="text-[12px] text-[#6B7280]">
          PDF, Word, Excel or email · up to 50 MB
        </p>
      </Upload.Dragger>

      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        initialValues={{ rolloutScope: "study" }}
        onValuesChange={(changed: Partial<FormShape>) => {
          if (changed.rolloutScope) setRolloutScope(changed.rolloutScope);
        }}
      >
        <Form.Item
          name="title"
          label="Title"
          rules={[{ required: true, message: "Give the document a title." }]}
        >
          <Input placeholder="e.g. Monitoring Plan — risk-based approach" />
        </Form.Item>

        <Form.Item
          name="documentClass"
          label="Document type"
          rules={[{ required: true, message: "Classify the document." }]}
        >
          <Select
            placeholder="Select a type"
            options={[
              { value: "protocol-class", label: "Protocol" },
              { value: "plan", label: "Plan" },
              { value: "correspondence", label: "Correspondence" },
              { value: "reference", label: "Reference" },
              { value: "other", label: "Other" },
            ]}
          />
        </Form.Item>

        <Form.Item name="rolloutScope" label="Rollout">
          <Radio.Group>
            <Radio.Button value="study">Study-wide</Radio.Button>
            <Radio.Button value="site">Per-site</Radio.Button>
          </Radio.Group>
        </Form.Item>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Form.Item
            name="amendmentLabel"
            label="Amendment / version identity"
            extra="Leave empty for an original issue."
          >
            <Input placeholder="e.g. Amendment 2, or v3.1" />
          </Form.Item>

          <Form.Item
            name="effectiveFrom"
            label="Effective from"
            rules={
              rolloutScope === "study"
                ? [
                    {
                      required: true,
                      message: "An effective date is required.",
                    },
                  ]
                : []
            }
            extra={
              rolloutScope === "site"
                ? "Sites adopt it individually after their own IRB/EC approval."
                : "Not the upload date."
            }
          >
            <DatePicker className="w-full" format="YYYY-MM-DD" />
          </Form.Item>
        </div>

        <Form.Item label="Who can see this document?">
          <Checkbox.Group
            className="flex flex-col gap-2"
            value={roleAccess}
            onChange={(values) => setRoleAccess(values as AssumedRole[])}
            options={ROLE_OPTIONS.map((option) => ({
              label: option.label,
              value: option.key,
            }))}
          />
        </Form.Item>

        <Form.Item name="note" label="Description (optional)">
          <Input.TextArea
            rows={2}
            placeholder="What this document is, and why it matters."
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}
